import _ from 'lodash';

// Import action types.
import ActionTypes from './actionTypes.js';

// Selectors.
import Selectors from './selectors.js';

// Config.
import Config from './config/config.js';
import Enums from './config/enums.js';

// Model business logic.
import { Logic } from './model.js';

// Misc dependencies.
import ConfigUtil from './utils/config.js';
import moduleUtils from './utils/module-utils';
import * as collectionUtils from './utils/collection-utils';

/**
 * <pre>
 * Goal module Actions.
 *
 * Summary of responsibilities:
 *
 *  - implements Redux action creators specific to component
 *
 * FYI:
 *
 * We are using {@link https://github.com/gaearon/redux-thunk|Redux Thunk} middleware on our {@link https://github.com/reactjs/redux|Redux} store.
 * For this reason you will see that inside each action creator, we return a function, rather than an action object directly.
 * </pre>
 *
 * @see {@link https://github.com/gaearon/redux-thunk|Redux Thunk Documentation}
 * @see {@link https://github.com/reactjs/redux|Redux Documentation}
 *
 * @author Tim Loram

 * @memberof module:UgcWall
 * @namespace
 *
 * @type {Object}
 */
const Actions = {
  /**
   * Register a component instance.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param initialConfig		{object} Any initial config to add to the state.
   */
  registerInstance: function(id, componentType, initialConfig) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.REGISTER_INSTANCE,
        id:                id,
        componentStateKey: componentStateKey,
        initialConfig:     initialConfig,
      });
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Fetch a set of display items for a given component id.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param phase 			{object} State of campaign phases.
   * @param showMoreMode 		{string} Replace/append items on existing list.
   * @param scope 			{string} Item scope (campaign, global, or both).
   * @param displayStrategy 	{string} Item prominence (Feature, show, spotlight etc).
   * @param socialVoting 		{boolean} Flag indicating if this module is in 'social' voting mode (vs entries voting).
   * @param params 			{object} Additional params relevant to making the api endpoint call.
   */
  fetchItems: function(
    id,
    componentType,
    phase,
    showMoreMode,
    scope,
    displayStrategy,
    socialVoting,
    params = {},
    overridePhase = null,
    shouldShowStickyContent,
    shouldAutoPoll
  ) {
    return (dispatch, getState) => {
      // - - - - - - - - - - - - - - - - - -
      // Get global config service.
      const globalConfig = NGXD.getServiceContainer().getService('config');

      // Get the app config specific to this module type (social wall/entries).
      const moduleTypeConfig = ConfigUtil.getConfigForModuleType(componentType);

      // Retrieve any updated values we need from the current state.
      const paging = Selectors.getPaging(getState(), componentType, id);
      const sorting = Selectors.getSorting(getState(), componentType, id);
      const filtering = Selectors.getFiltering(getState(), componentType, id);
      const collectionId = Selectors.getCollectionId(getState(), componentType, id);
      const campaignId = globalConfig.get('campaign.id');

      //[Victor]: WYN-1459 - we have replaced the check for allowConententServices with a check for current experience
      //version (>=70).
      const contentServicesVersion = globalConfig.get('contentServices.version');
      const experienceVersion = globalConfig.get('campaign.appVersion');
      const legacyMode = experienceVersion < 70;

      const allowAutoPolling = moduleUtils.allowAutoPolling(
        sorting,
        contentServicesVersion,
        showMoreMode,
        shouldAutoPoll,
        experienceVersion
      );

      // - - - - - - - - - - - - - - - - - -
      // Build the endpoint url
      const endpointPath = _.get(moduleTypeConfig, legacyMode ? 'api.version.v2.listLegacy' : 'api.version.v2.list');

      //Version 2
      const url = globalConfig.get('endpoints.contServSecure') + endpointPath;

      // - - - - - - - - - - - - - - - - - -
      // Build params for request.
      const fetchParams = Logic.Items.buildFetchParams(
        componentType,
        phase,
        overridePhase,
        paging,
        sorting,
        filtering,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        params,
        legacyMode,
        contentServicesVersion,
        campaignId,
        collectionId,
        shouldShowStickyContent
      );

      // - - - - - - - - - - - - - - - - - -
      // Get Ajax service.
      const ajaxService = NGXD.getServiceContainer().getService('ajax');

      // Get state key.
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      // - - - - - - - - - - - - - - - - - -
      // Execute async API call to fetch items.
      if (
        contentServicesVersion !== 'v2' ||
        (!!collectionId && collectionId !== 'INVALID_COLLECTION') ||
        (legacyMode && (scope === 'global' || scope === 'combined'))
      ) {
        // - - - - - - - - - - - - - - - - - -
        // Update isFetching.
        dispatch({
          type:              ActionTypes.IS_FETCHING,
          id:                id,
          componentStateKey: componentStateKey,
          isFetching:        true,
        });

        ajaxService
          .get(
            url,
            fetchParams

            // When response received.
          )
          .then(response => {
            if (response.statusText === 'OK' || response.status === 200) {
              // Default to no items in the response.
              let currentPage = 0;
              let totalPages = 0;
              let items = [];

              // If we received some content in the response.
              if (response.data.content) {
                currentPage = response.data.page;
                totalPages = response.data.pagecount;
                items = response.data.content;
                if (allowAutoPolling) {
                  Actions.fetchMinScore(id, componentType)(dispatch, getState);
                }
              }

              // - - - - - - - - - - - - - - - - - -
              // Contest phase logic >
              if (contentServicesVersion === 'v2' && componentType === 'entries') {
                // If we are in results phase, but got no results back.
                if (phase.results && overridePhase === null && items.length === 0) {
                  // Attempt to get finalist items instead...
                  Actions.fetchItems(
                    id,
                    componentType,
                    phase,
                    showMoreMode,
                    scope,
                    displayStrategy,
                    socialVoting,
                    params,
                    'finalist',
                    shouldShowStickyContent,
                    shouldAutoPoll
                  )(dispatch, getState);
                  return false;
                }

                // If we are in finalist phase, but got no results back.
                if (phase.finalist && (overridePhase === 'finalist' || !overridePhase) && items.length === 0) {
                  // Attempt to get normal entry items instead...
                  Actions.fetchItems(
                    id,
                    componentType,
                    phase,
                    showMoreMode,
                    scope,
                    displayStrategy,
                    socialVoting,
                    params,
                    'entry',
                    shouldShowStickyContent,
                    shouldAutoPoll
                  )(dispatch, getState);
                  return false;
                }
              }

              // Create display item entity instances from the returned JSON.
              const displayItems = Logic.Items.buildDisplayItemsFromResponse(items);

              // - - - - - - - - - - - - - - - - - -
              const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

              // - - - - - - - - - - - - - - - - - -
              // Update page count and current page.
              dispatch({
                type:              ActionTypes.UPDATE_PAGE_COUNT,
                id:                id,
                componentStateKey: componentStateKey,
                currentPage:       currentPage,
                totalPages:        totalPages,
              });

              // Receieve items.
              dispatch({
                type:              ActionTypes.RECEIVE_ITEMS,
                id:                id,
                componentStateKey: componentStateKey,
                showMoreMode:      fetchParams.showMoreMode,
                allowAutoPolling:  allowAutoPolling, // needed for content cap
                items:             displayItems,
                shouldShowStickyContent,
              });
            }

            // If error encountered.
          })
          .catch(function(error) {})
          .finally(() => {
            // - - - - - - - - - - - - - - - - - -
            // Update isFetching.
            dispatch({
              type:              ActionTypes.IS_FETCHING,
              id:                id,
              componentStateKey: componentStateKey,
              isFetching:        false,
            });

            // - - - - - - - - - - - - - - - - - -
            // Update has fetched.
            dispatch({
              type:              ActionTypes.HAS_FETCHED,
              id:                id,
              componentStateKey: componentStateKey,
            });
          });
      }
    };
  },

  fetchMinScore: (id, componentType) => {
    return (dispatch, getState) => {
      const collectionId = Selectors.getCollectionId(getState(), componentType, id);
      const globalConfig = NGXD.getServiceContainer().getService('config');
      const campaignId = globalConfig.get('campaign.id');
      const paging = Selectors.getPaging(getState(), componentType, id);
      const filtering = Selectors.getFiltering(getState(), componentType, id);
      const sorting = Selectors.getSorting(getState(), componentType, id);

      const fetchParams = Logic.Items.buildFetchParams(
        componentType,
        undefined, // phase
        undefined, // overridePhase
        paging,
        sorting,
        filtering,
        undefined, // showMoreMode
        undefined, // scope
        undefined, // displayStrategy
        undefined, // socialVoting
        undefined, // params
        false, // legacyMode (auto-polling not available in legacy mode)
        'v2',
        campaignId,
        collectionId
      );
      // ALWAYS 0 when fetching prev content
      fetchParams.offset = 0;

      const contentServicesVersion = globalConfig.get('contentServices.version');
      const url = globalConfig.get('endpoints.contServSecure') + '/api/content/3/score';
      const ajaxService = NGXD.getServiceContainer().getService('ajax');
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      if (contentServicesVersion !== 'v2' || (!!collectionId && collectionId !== 'INVALID_COLLECTION')) {
        ajaxService.get(url, fetchParams).then(response => {
          const minScore = _.get(response, 'data.minScore');
          const maxScore = _.get(response, 'data.maxScore');
          if (response.statusText === 'OK' || response.status === 200) {
            if (collectionUtils.isCollectionAscOrder(minScore, maxScore)) {
              // this will disable auto-polling for collection's with customOrderStrategy set to bottom.
              // TODO - BE will update maxScore/minScore names to be more representative of top/bottom content.
              // sort order is asc b/c collection 'customOrderStrategy' is bottom, BE determines this in intial fetch for content
              dispatch({
                type:              ActionTypes.SET_SORTING,
                id:                id,
                componentStateKey: componentStateKey,
                sortDir:           'asc',
              });
            } else {
              dispatch({
                type:              ActionTypes.SET_POLLING,
                id:                id,
                componentStateKey: componentStateKey,
                minScore:          _.get(response, 'data.minScore'),
                newItems:          [],
              });
            }
          }
        });
      }
    };
  },

  fetchPreviousItems: (id, moduleOpts) => {
    return (dispatch, getState) => {
      const globalConfig = NGXD.getServiceContainer().getService('config');
      const campaignId = globalConfig.get('campaign.id');
      const paging = Selectors.getPaging(getState(), moduleOpts.type, id);
      const collectionId = Selectors.getCollectionId(getState(), moduleOpts.type, id);
      const polling = Selectors.getPolling(getState(), moduleOpts.type, id);
      const items = Selectors.getItems(getState(), moduleOpts.type, id);
      const minScore = polling.minScore;
      const filtering = Selectors.getFiltering(getState(), moduleOpts.type, id);
      const sorting = Selectors.getSorting(getState(), moduleOpts.type, id);

      const url = globalConfig.get('endpoints.contServSecure') + '/api/content/3/score';

      const fetchParams = Logic.Items.buildFetchParams(
        moduleOpts.type,
        undefined, // phase
        undefined, // overridePhase
        paging,
        sorting,
        filtering,
        undefined, // showMoreMode
        undefined, // scope
        undefined, // displayStrategy
        undefined, // socialVoting
        undefined, // params
        false, // legacyMode (auto-polling not available in legacy mode)
        'v2',
        campaignId,
        collectionId,
        moduleOpts.shouldShowStickyContent,
        minScore
      );
      // ALWAYS 0 when fetching prev content
      fetchParams.offset = 0;

      const ajaxService = NGXD.getServiceContainer().getService('ajax');
      // - - - - - - - - - - - - - - - - - -
      // Execute async API call to fetch items.
      if (!!collectionId && collectionId !== 'INVALID_COLLECTION') {
        ajaxService
          .get(url, fetchParams)
          .then(response => {
            if (response.statusText === 'OK' || response.status === 200) {
              const newMinScore = _.get(response, 'data.minScore');
              const newMaxScore = _.get(response, 'data.maxScore');
              const componentStateKey = ConfigUtil.getStateKeyForModuleType(moduleOpts.type);
              // items that are moved to top in a custom ordered collection, would be returned when auto-polling
              // ignore those, so we don't get duplicated posts in display
              const dedupedItems = !!response.data.content ?
                _.compact(
                    response.data.content.map(item => {
                      if (!_.find(items, { id: item.id })) {
                        return item;
                      }
                    })
                  ) :
                [];
              if (collectionUtils.isCollectionAscOrder(newMinScore, newMaxScore)) {
                // this catches a change from collection 'customOrderStrategy' from 'top' to 'bottom' and stops auto-polling requests
                // TODO - BE will update maxScore/minScore names to be more representative of top/bottom content.
                // sort order is asc b/c collection 'customOrderStrategy' is bottom, BE determines this in fetch for content
                dispatch({
                  type:              ActionTypes.SET_SORTING,
                  id:                id,
                  componentStateKey: componentStateKey,
                  sortDir:           'asc',
                });
              } else if (!_.isEmpty(dedupedItems)) {
                const offset = paging.offset + dedupedItems.length;
                const newDisplayItems = Logic.Items.buildDisplayItemsFromResponse(dedupedItems);

                dispatch({
                  type:              ActionTypes.SET_POLLING,
                  id:                id,
                  componentStateKey: componentStateKey,
                  minScore:          newMinScore,
                  newItems:          newDisplayItems,
                });
                dispatch({
                  type:              ActionTypes.UPDATE_OFFSET,
                  id:                id,
                  componentStateKey: componentStateKey,
                  offset:            offset,
                });
                dispatch({
                  type:                    ActionTypes.RECEIVE_ITEMS,
                  id:                      id,
                  componentStateKey:       componentStateKey,
                  items:                   newDisplayItems,
                  showMoreMode:            Enums.SHOW_MORE_MODE_PREPEND,
                  shouldShowStickyContent: moduleOpts.shouldShowStickyContent,
                  allowAutoPolling:        moduleOpts.shouldAutoPoll,
                });
              } else {
                dispatch({
                  type:              ActionTypes.SET_POLLING,
                  id:                id,
                  componentStateKey: componentStateKey,
                  minScore:          minScore,
                  newItems:          [],
                });
              }
            }
          })
          .catch(function(error) {});
      }
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Page through a set of display items for a given component id.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param phase 			{object} State of campaign phases.
   * @param showMoreMode 		{string} Replace/append items on existing list.
   * @param scope 			{string} Item scope (campaign, global, or both).
   * @param displayStrategy 	{string} Item prominence (Feature, show, spotlight etc).
   * @param socialVoting 		{boolean} Flag indicating if this module is in 'social' voting mode (vs entries voting).
   * @param direction 		{string} The paging direction (asc or desc)
   */
  pageItems: function(
    id,
    componentType,
    phase,
    showMoreMode,
    scope,
    displayStrategy,
    socialVoting,
    direction,
    shouldShowStickyContent,
    shouldAutoPoll
  ) {
    return function(dispatch, getState) {
      // Retrieve any updated values we need from the current state.
      const paging = Selectors.getPaging(getState(), componentType, id);

      // - - - - - - - - - - - - - - - - - -
      // Build override params for this action.
      const params = Logic.Items.buildPagingOverrideParams(paging, showMoreMode, direction);

      // - - - - - - - - - - - - - - - - - -
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      // Update page offset.
      dispatch({
        type:              direction == 'prev' ? ActionTypes.PAGE_PREV : ActionTypes.PAGE_NEXT,
        id:                id,
        componentStateKey: componentStateKey,
        offset:            params.offset,
      });

      // Re-fetch items.
      Actions.fetchItems(
        id,
        componentType,
        phase,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        params,
        null,
        shouldShowStickyContent,
        shouldAutoPoll
      )(dispatch, getState);
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Sort a set of display items for a given component id.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param phase 			{object} State of campaign phases.
   * @param showMoreMode 		{string} Replace/append items on existing list.
   * @param scope 			{string} Item scope (campaign, global, or both).
   * @param displayStrategy 	{string} Item prominence (Feature, show, spotlight etc).
   * @param socialVoting 		{boolean} Flag indicating if this module is in 'social' voting mode (vs entries voting).
   * @param sortBy 			{string} Sorting column.
   * @param sortDir 			{string} Sorting order (ascending/descending).
   * @param totalItems 		{integer} The number of items currently displayed.
   */
  sortItems: function(
    id,
    componentType,
    phase,
    showMoreMode,
    scope,
    displayStrategy,
    socialVoting,
    sortBy,
    sortDir,
    totalItems,
    shouldShowStickyContent,
    shouldAutoPoll
  ) {
    return function(dispatch, getState) {
      // - - - - - - - - - - - - - - - - - -
      // Retrieve any updated values we need from the current state.
      const paging = Selectors.getPaging(getState(), componentType, id);

      // - - - - - - - - - - - - - - - - - -
      // Build override params for this action.
      const params = Logic.Items.buildSortingOverrideParams(paging, sortBy, sortDir, totalItems);

      // - - - - - - - - - - - - - - - - - -
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      // Update sorting.
      dispatch({
        type:              ActionTypes.SORT,
        id:                id,
        componentStateKey: componentStateKey,
        sortBy:            params.sort,
        sortDir:           params.order,
      });

      // Re-fetch items.
      Actions.fetchItems(
        id,
        componentType,
        phase,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        params,
        null,
        shouldShowStickyContent,
        shouldAutoPoll
      )(dispatch, getState);
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Filter a set of display items for a given component id.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param phase 			{object} State of campaign phases.
   * @param showMoreMode 		{string} Replace/append items on existing list.
   * @param scope 			{string} Item scope (campaign, global, or both).
   * @param displayStrategy 	{string} Item prominence (Feature, show, spotlight etc).
   * @param socialVoting 		{boolean} Flag indicating if this module is in 'social' voting mode (vs entries voting).
   * @param filters 			{object} Net set of filters to apply.
   * @param displayCategory 	{string} The default displayCategory (tag) as defined by the module.
   * @param totalItems 		{integer} The number of items currently displayed.
   */
  filterItems: function(
    id,
    componentType,
    phase,
    showMoreMode,
    scope,
    displayStrategy,
    socialVoting,
    filters,
    displayCategory,
    totalItems,
    shouldShowStickyContent,
    shouldAutoPoll
  ) {
    return function(dispatch, getState) {
      // - - - - - - - - - - - - - - - - - -
      // Retrieve any updated values we need from the current state.
      const paging = Selectors.getPaging(getState(), componentType, id);
      const existingFilters = Selectors.getFiltering(getState(), componentType, id);

      // - - - - - - - - - - - - - - - - - -
      // Build override params for this action.
      const params = Logic.Items.buildFilteringOverrideParams(paging, filters, displayCategory, totalItems);

      // - - - - - - - - - - - - - - - - - -
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      // Update filtering.
      dispatch({
        type:              ActionTypes.FILTER,
        id:                id,
        componentStateKey: componentStateKey,

        network:     params.hasOwnProperty('externalService') ? params.externalService : existingFilters.network,
        tag:         _.get(params, 'displayCategory', existingFilters.tag),
        user:        params.hasOwnProperty('filterUser') ? params.filterUser : existingFilters.user,
        contentType: params.contentType || existingFilters.type,
        date:        params.filterDate || existingFilters.date,
        entry:       {
          // Need to check for 'all' as item may have been de-selected not just empty!
          ...(params.filter1 !== 'all' && { filter1: params.filter1 || existingFilters.entry.filter1 }),
          ...(params.filter2 !== 'all' && { filter2: params.filter2 || existingFilters.entry.filter2 }),
          ...(params.filter3 !== 'all' && { filter3: params.filter3 || existingFilters.entry.filter3 }),
        },
      });

      // Need to set to empty as values are re-used and cannot be 'all'
      if (params.filter1 === 'all') {
        params.filter1 = '';
      }
      if (params.filter2 === 'all') {
        params.filter2 = '';
      }
      if (params.filter3 === 'all') {
        params.filter3 = '';
      }

      // Update page offset.
      dispatch({
        type:              ActionTypes.PAGE_FIRST,
        id:                id,
        componentStateKey: componentStateKey,
      });

      if (params.displayCategory === '') {
        delete params.displayCategory;
      }

      // Re-fetch items.
      Actions.fetchItems(
        id,
        componentType,
        phase,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        params,
        null,
        shouldShowStickyContent,
        shouldAutoPoll
      )(dispatch, getState);
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * View an individual item.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param itemId 			{integer} The id of the item we want to view.
   */
  viewItem: function(id, componentType, itemId) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.VIEW_ITEM,
        id:                id,
        componentStateKey: componentStateKey,
        itemId:            itemId,
      });
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Close an individual item.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   */
  closeItem: function(id, componentType) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.CLOSE_ITEM,
        id:                id,
        componentStateKey: componentStateKey,
      });
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Clear return focus to state
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   */
  clearReturnFocusTo: function(id, componentType) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.CLEAR_RETURN_FOCUS,
        id:                id,
        componentStateKey: componentStateKey,
      });
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Set custom items.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param items 			{object} The custom items.
   */
  setCustomItems: function(id, componentType, items) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.RECEIVE_CUSTOM_ITEMS,
        id:                id,
        componentStateKey: componentStateKey,
        items:             items,
      });
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Set active state.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param isActive 			{boolean} Is this components parent state active?
   */
  setActiveState: function(id, componentType, isActive) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.SET_ACTIVE_STATE,
        id:                id,
        componentStateKey: componentStateKey,
        isActiveState:     isActive,
      });
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Set collection id.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param collectionId 		{string} The string id for the collection.
   */
  setCollectionId: function(id, componentType, collectionId) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.SET_COLLECTION_ID,
        id:                id,
        componentStateKey: componentStateKey,
        collectionId:      collectionId,
      });
    };
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Set boolean to indicate we need to use an external link handler.
   *
   * @param id 				{string} The DOM id of the module.
   * @param componentType 	{string} The component type of the module.
   * @param useHandler 		{boolean} Should we use the external handler?
   */
  setUseExternalLinkHandler: function(id, componentType, useHandler) {
    return function(dispatch) {
      const componentStateKey = ConfigUtil.getStateKeyForModuleType(componentType);

      dispatch({
        type:              ActionTypes.SET_USE_EXTERNAL_LINK_HANDLER,
        id:                id,
        componentStateKey: componentStateKey,
        useHandler:        useHandler,
      });
    };
  },
};

export default Actions;
